/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { useState, useEffect, useRef, FormEvent, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { 
  BarChart3, 
  Globe, 
  Mail, 
  MessageSquare, 
  Phone, 
  ArrowRight, 
  CheckCircle2, 
  MapPin, 
  Menu, 
  X, 
  ChevronDown, 
  Target, 
  ShieldCheck,
  PhoneCall,
  Shield,
  RefreshCw,
  AlertTriangle,
  Loader2
} from "lucide-react";
import { SERVICES } from "./data/services";
import { saveVideoBlob, loadVideoBlob } from "./utils/media";

// Lazy load non-homepage route pages to minimize initial bundle size
const AboutPageContent = lazy(() => import("./pages/AboutPage"));
const DotsonHubPageContent = lazy(() => import("./pages/DotsonHubPage"));
const WorkWithUsPageContent = lazy(() => import("./pages/WorkWithUsPage"));
const ServicePage = lazy(() => import("./pages/ServicePage"));
const PrivacyPageContent = lazy(() => import("./pages/PrivacyPage"));
const TermsPageContent = lazy(() => import("./pages/TermsPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const isDarkHeroPage = location.pathname === "/" || location.pathname === "/work-with-us";
  const navTextColor = isScrolled || !isDarkHeroPage ? "text-brand-900" : "text-white";
  const navBgColor = isScrolled || !isDarkHeroPage ? "bg-white/90 backdrop-blur-md shadow-sm" : "bg-transparent";

  const navItems = [
    { name: "Client Login", href: "https://hub.dotson.digital", isExternal: true },
    { name: "Contact", to: isHomePage ? "#contact" : "/#contact", isHash: true, hashId: "contact" },
    { name: "About", to: "/about", isHash: false },
    { name: "Work With Us", to: "/work-with-us", isHash: false }
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBgColor} ${isScrolled ? "py-4" : "py-6"}`}>
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group">
          <img
            src="/dotsondigital-logo.webp"
            srcSet="/dotsondigital-logo.webp 1x, /dotsondigital-logo-80.webp 2x"
            alt="Dotson Digital"
            width={40}
            height={40}
            className="w-10 h-10 object-contain drop-shadow-sm transition-transform group-hover:scale-105"
            loading="eager"
            decoding="async"
            referrerPolicy="no-referrer"
          />
          <span 
            style={{ fontFamily: 'var(--font-heading)' }}
            className={`text-2xl font-extrabold tracking-tight ${navTextColor}`}
          >
            DOTSON<span className="text-brand-500">DIGITAL</span>
          </span>
        </Link>

        {/* Desktop Menu */}
        <div className="hidden md:flex items-center gap-8">
          <div 
            className="relative group"
            onMouseEnter={() => setIsDropdownOpen(true)}
            onMouseLeave={() => setIsDropdownOpen(false)}
          >
            <button className={`flex items-center gap-1 font-medium transition-colors hover:text-brand-500 ${navTextColor}`}>
              Services <ChevronDown className={`w-4 h-4 transition-transform ${isDropdownOpen ? "rotate-180" : ""}`} />
            </button>
            {isDropdownOpen && (
              <div
                className="absolute top-full left-0 w-64 bg-white rounded-2xl shadow-xl border border-gray-100 py-4 mt-2 overflow-hidden animate-fadeIn"
              >
                {SERVICES.map((service) => (
                  <Link
                    key={service.slug}
                    to={`/services/${service.slug}`}
                    className="block px-6 py-3 text-gray-600 hover:bg-brand-50 hover:text-brand-600 transition-colors font-medium"
                    onClick={() => setIsDropdownOpen(false)}
                  >
                    {service.title}
                  </Link>
                ))}
              </div>
            )}
          </div>
          
          {navItems.map((item) => (
            item.isExternal ? (
              <a 
                key={item.name}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                className={`font-medium transition-colors hover:text-brand-500 ${navTextColor}`}
              >
                {item.name}
              </a>
            ) : (
              <Link 
                key={item.name} 
                to={item.to} 
                className={`font-medium transition-colors hover:text-brand-500 ${navTextColor}`}
                onClick={(e) => {
                  if (item.isHash && isHomePage) {
                    e.preventDefault();
                    document.getElementById(item.hashId)?.scrollIntoView({ behavior: "smooth" });
                  }
                }}
              >
                {item.name}
              </Link>
            )
          ))}
          <Link 
            to="/#contact" 
            className="bg-brand-600 hover:bg-brand-700 text-white px-6 py-2.5 rounded-full font-semibold transition-all shadow-lg shadow-brand-600/20"
          >
            Get Started
          </Link>
        </div>

        {/* Mobile Toggle */}
        <button 
          aria-label={isMobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
          className={navTextColor}
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? <X /> : <Menu />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div 
          className="absolute top-full left-0 right-0 bg-white border-t p-6 shadow-xl md:hidden overflow-hidden animate-fadeIn"
        >
          <div className="flex flex-col gap-4">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Services</p>
            <div className="grid grid-cols-1 gap-2 pl-4 border-l-2 border-brand-100">
              {SERVICES.map((service) => (
                <Link
                  key={service.slug}
                  to={`/services/${service.slug}`}
                  className="text-lg font-medium text-gray-600"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {service.title}
                </Link>
              ))}
            </div>
            <div className="h-px bg-gray-100 my-2" />
            {navItems.map((item) => (
              item.isExternal ? (
                <a 
                  key={item.name}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-lg font-medium text-gray-600"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.name}
                </a>
              ) : (
                <Link 
                  key={item.name} 
                  to={item.to} 
                  className="text-lg font-medium text-gray-600"
                  onClick={(e) => {
                    setIsMobileMenuOpen(false);
                    if (item.isHash && isHomePage) {
                      e.preventDefault();
                      document.getElementById(item.hashId)?.scrollIntoView({ behavior: "smooth" });
                    }
                  }}
                >
                  {item.name}
                </Link>
              )
            ))}
            <Link 
              to="/#contact" 
              className="bg-brand-600 text-white px-6 py-3 rounded-xl font-semibold text-center"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Get Started
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

const Hero = () => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const targetTimeRef = useRef<number>(0);
  const isSeekingRef = useRef<boolean>(false);
  const rafRef = useRef<number | null>(null);
  const [videoSrc, setVideoSrc] = useState<string>("/me.mp4");
  const [isDragOver, setIsDragOver] = useState<boolean>(false);
  const [pillsVisible, setPillsVisible] = useState(false);

  useEffect(() => {
    loadVideoBlob((blobUrl) => {
      setVideoSrc(blobUrl);
    });
  }, []);

  const handleFileSelected = async (file?: File | null) => {
    if (file && (file.type.startsWith("video/") || file.name.endsWith(".mp4") || file.name.endsWith(".webm"))) {
      const url = URL.createObjectURL(file);
      setVideoSrc(url);
      saveVideoBlob(file);

      try {
        await fetch("/api/upload-video", {
          method: "POST",
          headers: { "Content-Type": file.type || "video/mp4" },
          body: file,
        });
      } catch (err) {
        console.error("Failed to upload video to server", err);
      }
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setPillsVisible(true);
    }, 400);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    isSeekingRef.current = false;

    const performSeek = () => {
      if (!video || !video.duration || isNaN(video.duration)) return;
      if (!isSeekingRef.current && !video.seeking) {
        if (Math.abs(video.currentTime - targetTimeRef.current) > 0.015) {
          isSeekingRef.current = true;
          video.currentTime = targetTimeRef.current;
        }
      }
    };

    const handleLoadedMetadata = () => {
      if (video.duration && !isNaN(video.duration)) {
        // Set initial look to middle
        const initial = video.duration * 0.5;
        targetTimeRef.current = initial;
        video.currentTime = initial;
      }
    };

    const handleSeeked = () => {
      isSeekingRef.current = false;
      if (video && !isNaN(video.duration)) {
        if (Math.abs(video.currentTime - targetTimeRef.current) > 0.015) {
          isSeekingRef.current = true;
          video.currentTime = targetTimeRef.current;
        }
      }
    };

    // Directly track horizontal cursor position across the window (0 to 1)
    const updateTargetFromCursor = (clientX: number) => {
      if (!video || !video.duration || isNaN(video.duration)) return;
      const normalizedX = Math.max(0, Math.min(1, clientX / window.innerWidth));
      targetTimeRef.current = normalizedX * video.duration;
      performSeek();
    };

    const handleMouseMove = (e: MouseEvent) => {
      updateTargetFromCursor(e.clientX);
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 0) {
        updateTargetFromCursor(e.touches[0].clientX);
      }
    };

    // RAF loop to ensure smooth, responsive tracking of the cursor position
    const tick = () => {
      performSeek();
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("seeked", handleSeeked);
    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    window.addEventListener("touchmove", handleTouchMove, { passive: true });

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("seeked", handleSeeked);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("touchmove", handleTouchMove);
    };
  }, [videoSrc]);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section 
      onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragOver(false);
        handleFileSelected(e.dataTransfer.files?.[0]);
      }}
      className={`relative min-h-screen flex items-center justify-center pt-24 pb-16 overflow-hidden bg-brand-950 transition-all ${
        isDragOver ? "ring-4 ring-brand-400 ring-inset" : ""
      }`}
    >
      {/* Background Video (reacts to mouse cursor position) */}
      <div className="absolute inset-0 w-full h-full overflow-hidden pointer-events-none">
        <video
          ref={videoRef}
          key={videoSrc}
          muted
          playsInline
          preload="none"
          poster="/me-tiny.webp"
          src={videoSrc}
          className="w-full h-full object-cover select-none filter brightness-95"
          style={{ objectPosition: "center 30%" }}
        />
        {/* Balanced color overlay for readability while keeping the character vibrant */}
        <div className="absolute inset-0 bg-brand-950/40 mix-blend-multiply" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-950/85 via-brand-900/40 to-brand-950/85" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-950 via-transparent to-brand-950/70" />
      </div>

      {/* Decorative Glow Elements */}
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-brand-500 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-brand-600 rounded-full blur-[120px]" />
      </div>
      
      <div className="max-w-4xl mx-auto px-6 relative z-10 text-center flex flex-col items-center">
        <div className="flex flex-col items-center">
          {/* Badge */}
          <div className="inline-flex items-center justify-center gap-2 px-4 py-1.5 rounded-full bg-brand-500/15 border border-brand-500/30 text-brand-100 text-xs sm:text-sm font-semibold mb-6 backdrop-blur-md">
            <span className="flex h-2 w-2 rounded-full bg-brand-400 animate-pulse" />
            <span>Results Driven. Verified Experience. No Retainers.</span>
          </div>

          <h1 className="text-4xl sm:text-6xl md:text-7xl font-extrabold text-white leading-[1.08] tracking-tight mb-6 max-w-3xl">
            We Build <span className="text-brand-400">Digital Engines</span> That Book Clients.
          </h1>

          <p className="text-lg sm:text-xl md:text-2xl text-brand-100/90 mb-8 max-w-2xl mx-auto leading-relaxed font-normal">
            Dotson Digital transforms your online presence into a revenue-generating machine through data-backed strategies and creative excellence. Book jobs instead of chasing leads.
          </p>

          {/* Action pill buttons from template */}
          <div 
            className={`flex flex-wrap items-center justify-center gap-y-1 mb-8 transition-all duration-400 ease-out ${
              pillsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2 pointer-events-none"
            }`}
          >
            <button
              type="button"
              onClick={() => scrollToSection("contact")}
              className="inline-flex items-center justify-center bg-white text-black border border-black/10 rounded-full text-[13px] sm:text-[15px] px-4 sm:px-5 py-[0.3em] mx-[0.2em] mb-[0.4em] whitespace-nowrap hover:bg-black hover:text-white transition-colors duration-200 cursor-pointer font-medium shadow-sm"
            >
              Pitch us an idea
            </button>

            <button
              type="button"
              onClick={() => scrollToSection("services")}
              className="inline-flex items-center justify-center bg-white text-black border border-black/10 rounded-full text-[13px] sm:text-[15px] px-4 sm:px-5 py-[0.3em] mx-[0.2em] mb-[0.4em] whitespace-nowrap hover:bg-black hover:text-white transition-colors duration-200 cursor-pointer font-medium shadow-sm"
            >
              Explore Services
            </button>

            <a
              href="https://hub.dotson.digital"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center bg-white text-black border border-black/10 rounded-full text-[13px] sm:text-[15px] px-4 sm:px-5 py-[0.3em] mx-[0.2em] mb-[0.4em] whitespace-nowrap hover:bg-black hover:text-white transition-colors duration-200 cursor-pointer font-medium shadow-sm"
            >
              Dotson HUB
            </a>

            <button
              type="button"
              onClick={() => scrollToSection("process")}
              className="inline-flex items-center justify-center bg-white text-black border border-black/10 rounded-full text-[13px] sm:text-[15px] px-4 sm:px-5 py-[0.3em] mx-[0.2em] mb-[0.4em] whitespace-nowrap hover:bg-black hover:text-white transition-colors duration-200 cursor-pointer font-medium shadow-sm"
            >
              See how we operate
            </button>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link 
              to="/#contact"
              onClick={(e) => {
                const el = document.getElementById("contact");
                if (el) {
                  e.preventDefault();
                  el.scrollIntoView({ behavior: "smooth" });
                }
              }}
              className="w-full sm:w-auto bg-brand-600 hover:bg-brand-500 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-2 group shadow-xl shadow-brand-600/30 hover:scale-[1.02]"
            >
              Start Your Project
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

const CERTIFICATIONS = [
  {
    title: "Google Digital Marketing & E-commerce",
    credentialId: "92eb424c-946b-493f-b5ee-5fe12b8455eb",
    url: "https://www.credly.com/badges/92eb424c-946b-493f-b5ee-5fe12b8455eb/public_url",
    image: "/google-digital-marketing-cert.webp",
  },
  {
    title: "Google Cybersecurity Professional",
    credentialId: "a1c703ad-9494-49f6-9062-e764e699a151",
    url: "https://www.credly.com/badges/a1c703ad-9494-49f6-9062-e764e699a151/public_url",
    image: "/google-cybersecurity-cert.webp",
  },
  {
    title: "Google Data Analytics Professional",
    credentialId: "c5b9f9c9-b200-41d6-8fbc-b191e84ce8eb",
    url: "https://www.credly.com/badges/c5b9f9c9-b200-41d6-8fbc-b191e84ce8eb/public_url",
    image: "/google-analytics-cert.webp",
  },
];

const Certifications = () => {
  return (
    <div className="flex flex-wrap justify-center items-stretch gap-6 sm:gap-8 mt-12">
      {CERTIFICATIONS.map((cert) => (
        <a
          key={cert.credentialId}
          href={cert.url}
          target="_blank"
          rel="noopener noreferrer"
          title={`Verify ${cert.title} credential on Credly`}
          className="group flex flex-col items-center justify-between p-5 rounded-2xl bg-white border border-gray-200 hover:border-brand-500 hover:shadow-xl transition-all duration-300 w-48 hover:-translate-y-1 shadow-sm"
        >
          <div className="w-[105px] h-[105px] flex items-center justify-center mb-4">
            <img
              src={cert.image}
              alt={cert.title}
              width={105}
              height={105}
              className="w-[105px] h-[105px] object-contain drop-shadow-sm group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
              decoding="async"
              referrerPolicy="no-referrer"
            />
          </div>
          <div className="text-center w-full">
            <span className="block text-xs font-bold text-gray-900 leading-snug line-clamp-2 mb-2">
              {cert.title}
            </span>
            <span className="inline-flex items-center gap-1 text-xs font-bold text-brand-700 group-hover:text-brand-800 transition-colors">
              <span>Verified Credential</span>
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
            </span>
          </div>
        </a>
      ))}
    </div>
  );
};

const GoogleBusinessProfileMastery = () => {
  return (
    <div className="mt-14 mb-20 p-8 sm:p-12 lg:p-16 rounded-[2.5rem] bg-gradient-to-b from-brand-950 via-brand-900 to-brand-950 text-white border border-brand-800/80 shadow-2xl relative overflow-hidden">
      {/* Ambient background accents */}
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-brand-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-brand-400/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-5xl mx-auto">
        {/* Urgent Badge */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-500/15 border border-brand-500/30 text-brand-300 text-xs sm:text-sm font-semibold tracking-wide uppercase">
            <span className="flex h-2 w-2 rounded-full bg-brand-400 animate-ping" />
            High-Intent Search Dominance
          </div>
        </div>

        {/* 1. Main Headline (The Hook) */}
        <h3 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white text-center tracking-tight leading-[1.15] mb-8 max-w-4xl mx-auto">
          Stop Treating Your Website Like Your Front Door. <span className="text-brand-400">Command the Google Map Pack</span> and Capture Customers Before They Ever Click Your Competitors.
        </h3>

        {/* 2. Explanatory Paragraph (What/Why it matters) */}
        <div className="max-w-3xl mx-auto text-center text-base sm:text-lg text-brand-100/90 leading-relaxed space-y-4 mb-14">
          <p>
            Relying solely on a website to generate business is an outdated gamble. Today&apos;s high-intent buyers don&apos;t browse endless pages or scroll through traditional search results, they search with immediate buying intent and make decisive purchasing choices in seconds directly on Google Maps.
          </p>
          <p>
            Your Google Business Profile is the most valuable piece of digital real estate your business owns. It delivers immediate, high-trust buying signals, verified customer reviews, authentic photos, real-time operating hours, and a direct one-tap call button, that bypass slow web searching entirely. This instantly qualifies your business and converts casual searchers into direct phone calls, walk-ins, and high-value inquiries before they ever see a competitor&apos;s website.
          </p>
          <p className="font-semibold text-brand-300">
            Whether you operate a retail storefront, professional consulting firm, healthcare practice, or modern service company, neglecting this single critical touchpoint puts a strict ceiling on your revenue while competitors capture the sales that should be yours.
          </p>
        </div>

        {/* 3. The Three Pillars of Mastery (The Benefits Grid) */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 mb-12">
          {/* Pillar 1: Max Visibility */}
          <div className="p-8 rounded-3xl bg-white/[0.04] border border-white/10 hover:border-brand-400/40 transition-all flex flex-col justify-between group">
            <div>
              <div className="w-14 h-14 rounded-2xl bg-brand-500/20 border border-brand-500/30 flex items-center justify-center text-brand-400 mb-6 group-hover:scale-110 transition-transform">
                <MapPin className="w-7 h-7" />
              </div>
              <div className="text-xs uppercase tracking-wider font-bold text-brand-400 mb-2">Pillar 1</div>
              <h4 className="text-2xl font-bold text-white mb-3">Maximum Visibility</h4>
              <p className="text-brand-200 text-sm font-medium mb-6 leading-relaxed">
                Dominate the Map Pack and get found the exact second ready-to-buy customers search.
              </p>
              <ul className="space-y-3.5 text-brand-100/90 text-sm">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-4 h-4 text-brand-400 mt-1 shrink-0" />
                  <span><strong>Top-3 Map Pack Placement:</strong> Own prime positioning where over 80% of local buying clicks and phone inquiries originate.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-4 h-4 text-brand-400 mt-1 shrink-0" />
                  <span><strong>Instant Discovery:</strong> Appear at the undisputed top of mobile search results when buyers need immediate solutions.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-4 h-4 text-brand-400 mt-1 shrink-0" />
                  <span><strong>Market Foreclosure:</strong> Push competing businesses below the fold so your company is the only natural choice.</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Pillar 2: Trust & Credibility */}
          <div className="p-8 rounded-3xl bg-white/[0.04] border border-white/10 hover:border-brand-400/40 transition-all flex flex-col justify-between group">
            <div>
              <div className="w-14 h-14 rounded-2xl bg-brand-500/20 border border-brand-500/30 flex items-center justify-center text-brand-400 mb-6 group-hover:scale-110 transition-transform">
                <ShieldCheck className="w-7 h-7" />
              </div>
              <div className="text-xs uppercase tracking-wider font-bold text-brand-400 mb-2">Pillar 2</div>
              <h4 className="text-2xl font-bold text-white mb-3">Trust &amp; Credibility</h4>
              <p className="text-brand-200 text-sm font-medium mb-6 leading-relaxed">
                Turn skeptical searchers into committed buyers through undeniable social proof and authority.
              </p>
              <ul className="space-y-3.5 text-brand-100/90 text-sm">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-4 h-4 text-brand-400 mt-1 shrink-0" />
                  <span><strong>Review Supremacy:</strong> Compound consistent 5-star customer feedback that shatters buyer doubt before they call.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-4 h-4 text-brand-400 mt-1 shrink-0" />
                  <span><strong>Visual Proof:</strong> Display authentic facility, team, and project imagery that proves capability and commands premium rates.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-4 h-4 text-brand-400 mt-1 shrink-0" />
                  <span><strong>Cross-Industry Authority:</strong> Establish elite trust that works across retail stores, professional practices, and B2B firms.</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Pillar 3: Actionable Leads */}
          <div className="p-8 rounded-3xl bg-white/[0.04] border border-white/10 hover:border-brand-400/40 transition-all flex flex-col justify-between group">
            <div>
              <div className="w-14 h-14 rounded-2xl bg-brand-500/20 border border-brand-500/30 flex items-center justify-center text-brand-400 mb-6 group-hover:scale-110 transition-transform">
                <PhoneCall className="w-7 h-7" />
              </div>
              <div className="text-xs uppercase tracking-wider font-bold text-brand-400 mb-2">Pillar 3</div>
              <h4 className="text-2xl font-bold text-white mb-3">Actionable Leads</h4>
              <p className="text-brand-200 text-sm font-medium mb-6 leading-relaxed">
                Turn passive search impressions into incoming phone calls, quote requests, and booked revenue.
              </p>
              <ul className="space-y-3.5 text-brand-100/90 text-sm">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-4 h-4 text-brand-400 mt-1 shrink-0" />
                  <span><strong>One-Tap Inbound Calls:</strong> Remove every friction barrier with direct phone dialing straight into your office or intake desk.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-4 h-4 text-brand-400 mt-1 shrink-0" />
                  <span><strong>Pre-Qualified Prospects:</strong> Attract prospects who already checked your reviews, hours, and credentials and are ready to buy.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-4 h-4 text-brand-400 mt-1 shrink-0" />
                  <span><strong>Predictable Sales Pipeline:</strong> Generate reliable, day-in day-out client inquiries without paying recurring ad tolls.</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Action Prompt */}
        <div className="text-center pt-2">
          <Link
            to="/#contact"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="inline-flex items-center gap-3 bg-brand-500 hover:bg-brand-400 text-brand-950 font-extrabold px-8 py-4 rounded-2xl transition-all shadow-lg shadow-brand-500/20 hover:scale-[1.02]"
          >
            Claim Your Local Visibility Audit <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="text-xs text-brand-300/80 mt-3">
            Stop losing high-value clients to competitors who rank above you.
          </p>
        </div>
      </div>
    </div>
  );
};

const Services = () => {
  return (
    <section id="services" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center max-w-4xl mx-auto mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-brand-900 mb-6 tracking-tight leading-tight">
            From Visibility to Revenue: <span className="text-brand-600">Your Digital Foundation</span> Built for Today's Search Landscape
          </h2>
          <div className="text-lg text-gray-600 leading-relaxed space-y-3 max-w-3xl mx-auto">
            <p>
              We don't just build websites, we construct revenue machines optimized for search engine dominance.
            </p>
            <p>
              We start by mastering local SEO, Google visibility, your Google Business Profile and emerging AI platforms, we ensure your business is always positioned at the top of customer mindshare when they need you most.
            </p>
          </div>
          <Certifications />
        </div>

        {/* Google Business Profile & Map Pack Authority Section */}
        <GoogleBusinessProfileMastery />

        {/* Full Strategic Capabilities */}
        <div className="pt-6 border-t border-gray-100">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h3 className="text-2xl sm:text-3xl font-bold text-brand-900 mb-3">
              Full-Spectrum Digital Infrastructure
            </h3>
            <p className="text-gray-600">
              Explore our core technical services engineered to protect, scale, and accelerate your business.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {SERVICES.map((service, i) => (
              <div
                key={i}
                className="p-8 rounded-3xl bg-brand-50 border border-brand-100 transition-all hover:-translate-y-2 hover:shadow-xl hover:shadow-brand-500/5 group flex flex-col h-full duration-300"
              >
                <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center text-brand-600 shadow-sm mb-6 group-hover:bg-brand-600 group-hover:text-white transition-colors">
                  <service.icon className="w-7 h-7" />
                </div>
                <h3 className="text-2xl font-bold text-brand-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed mb-8 flex-grow">
                  {service.description}
                </p>
                <Link 
                  to={`/services/${service.slug}`}
                  aria-label={`Learn more about ${service.title}`}
                  className="inline-flex items-center gap-2 text-brand-600 font-bold group-hover:gap-3 transition-all"
                >
                  Learn More <span className="sr-only">about {service.title}</span> <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const WhyChooseUs = () => {
  const reasons = [
    {
      title: "Data-Driven Strategies",
      desc: "We don't guess; we test. Every decision we make is backed by rigorous data analysis and performance metrics to ensure your marketing budget is spent effectively.",
      icon: BarChart3
    },
    {
      title: "Local Expertise, Global Reach",
      desc: "Based in Grovetown, GA, we understand the local market dynamics while leveraging world-class digital tools to help your business compete on a global scale.",
      icon: Globe
    },
    {
      title: "Transparent Reporting",
      desc: "You'll never be in the dark about your campaign's performance. We provide detailed, easy-to-understand reports that show exactly how your business is growing.",
      icon: ShieldCheck
    },
    {
      title: "Customized Solutions",
      desc: "Your business is unique, and your marketing should be too. We create bespoke strategies tailored specifically to your goals, industry, and target audience.",
      icon: Target
    }
  ];

  return (
    <section className="py-24 bg-brand-900 text-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-20 items-center">
          <div>
            <h2 className="text-4xl md:text-5xl font-bold mb-8 leading-tight">
              Why Partner with <span className="text-brand-400">Dotson Digital</span> for Your Growth?
            </h2>
            <p className="text-xl text-brand-100/80 mb-12 leading-relaxed">
              In today's crowded digital landscape, you need more than just a service provider. You need a strategic partner dedicated to your long-term success. At Dotson Digital, we combine technical mastery with creative innovation to deliver results that matter.
            </p>
            <div className="grid sm:grid-cols-2 gap-8">
              {reasons.map((reason, i) => (
                <div key={i} className="space-y-4">
                  <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center text-brand-400">
                    <reason.icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-bold">{reason.title}</h3>
                  <p className="text-brand-200 leading-relaxed">{reason.desc}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="aspect-square rounded-[4rem] overflow-hidden border border-white/20 flex items-center justify-center p-8 sm:p-12 relative z-10 shadow-2xl group bg-brand-950">
              {/* Background Character Image */}
              <img
                src="/me looking right-tinypng.io.webp"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).src = "/me.png";
                }}
                alt="Dotson Digital Growth Strategy and Client ROI"
                className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                loading="eager"
                decoding="async"
                referrerPolicy="no-referrer"
              />
              {/* Vibrant Blue Overlay - keeps the character visible while making text pop */}
              <div className="absolute inset-0 bg-brand-950/35 mix-blend-multiply pointer-events-none" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-950/90 via-brand-950/30 to-brand-900/40 pointer-events-none" />
              
              {/* Content Overlay */}
              <div className="text-center relative z-10 p-6 sm:p-8 rounded-3xl bg-brand-950/60 backdrop-blur-md border border-white/15 shadow-2xl max-w-xs transition-transform group-hover:scale-105 duration-300">
                <div className="text-7xl sm:text-8xl font-black text-brand-400 mb-3 tracking-tight drop-shadow-[0_4px_12px_rgba(0,0,0,0.8)]">10X</div>
                <div className="text-xl sm:text-2xl font-bold text-white leading-snug drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">Average ROI for Clients</div>
              </div>
            </div>
            <div className="absolute -top-20 -right-20 w-64 h-64 bg-brand-500/25 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-brand-400/25 rounded-full blur-3xl pointer-events-none" />
          </div>
        </div>
      </div>
    </section>
  );
};

const OurProcess = () => {
  const steps = [
    {
      title: "Discovery & Audit",
      desc: "We start by understanding your business, your goals, and your current digital footprint. Our comprehensive audit identifies gaps and opportunities for immediate growth."
    },
    {
      title: "Strategy Development",
      desc: "Based on our findings, we craft a multi-channel digital strategy designed to reach your target audience where they spend their time online."
    },
    {
      title: "Implementation & Launch",
      desc: "Our team of experts executes the plan, from technical SEO fixes and content creation to high-performance PPC campaign setup."
    },
    {
      title: "Optimization & Scaling",
      desc: "Digital marketing is never 'done'. We continuously monitor performance, running A/B tests and adjusting tactics to maximize your return on investment."
    }
  ];

  return (
    <section id="process" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <h2 className="text-4xl font-bold text-brand-900 mb-6">Our Proven Process for Success</h2>
          <p className="text-lg text-gray-600">
            We've refined our methodology over years of working with diverse clients. Our systematic approach ensures consistency, quality, and measurable results every time.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {steps.map((step, i) => (
            <div key={i} className="relative flex flex-col">
              <div className="w-14 h-14 rounded-2xl bg-brand-50 border border-brand-200/80 text-brand-700 font-extrabold text-xl flex items-center justify-center mb-6 shadow-xs select-none">
                0{i + 1}
              </div>
              <div className="relative z-10">
                <h3 className="text-2xl font-bold text-brand-900 mb-4">{step.title}</h3>
                <p className="text-gray-600 leading-relaxed">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const FAQ = () => {
  const faqs = [
    {
      q: "How long does it take to see results from SEO?",
      a: "SEO is a long-term strategy. While some technical improvements can show results in weeks, significant ranking and traffic growth typically take 3 to 6 months of consistent effort."
    },
    {
      q: "What makes Dotson Digital different from other agencies?",
      a: "We focus on ROI, not just vanity metrics. We are a boutique agency based in Grovetown, GA, which means you get personalized attention from senior strategists, not an entry-level account manager."
    },
    {
      q: "Do you work with small businesses or large corporations?",
      a: "We work with both! Our strategies are scalable. Whether you're a local Grovetown business looking for more foot traffic or a national brand seeking to dominate your niche, we have the expertise to help."
    },
    {
      q: "How do you measure the success of a campaign?",
      a: "We align our KPIs with your business goals. This usually includes metrics like organic traffic growth, conversion rate, cost per lead, and total revenue generated from digital channels."
    }
  ];

  return (
    <section className="py-24 bg-brand-50">
      <div className="max-w-4xl mx-auto px-6">
        <h2 className="text-4xl font-bold text-brand-900 mb-12 text-center">Frequently Asked Questions</h2>
        <div className="space-y-6">
          {faqs.map((faq, i) => (
            <div key={i} className="bg-white p-8 rounded-2xl shadow-sm border border-brand-100">
              <h3 className="text-xl font-bold text-brand-900 mb-4">{faq.q}</h3>
              <p className="text-gray-600 leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const LocalSEOSection = () => {
  return (
    <section className="py-24 bg-white border-t border-brand-100">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="order-2 lg:order-1">
            <div className="aspect-video rounded-[3rem] overflow-hidden shadow-2xl relative group bg-brand-950 border border-brand-100">
              <img 
                src="/georgia-state.webp" 
                alt="State of Georgia, USA" 
                width="800"
                height="450"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                referrerPolicy="no-referrer"
              />
              <div className="absolute inset-0 bg-brand-950/15 group-hover:bg-brand-950/5 transition-colors pointer-events-none" />
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <h2 className="text-4xl sm:text-5xl font-extrabold text-brand-950 mb-8 leading-tight">
              Stop Being Invisible. Start <span className="text-brand-600">OWNING</span> Your Local Market
            </h2>
            <div className="prose prose-lg text-gray-600 max-w-none space-y-6">
              <p>
                As a digital marketing agency rooted in the community of Grovetown, GA, we bring the heart of local expertise combined with best-in-class, US-level digital strategies. We understand that being successful today requires more than just showing up, it requires strategic dominance.
              </p>
              <p>
                We don't simply manage websites; we build scalable lead generation systems designed to maximize your real-world revenue. Our comprehensive approach focuses on three interconnected pillars crucial for modern growth:
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-4">
              <div className="px-6 py-3 bg-brand-50 rounded-full text-brand-700 font-bold border border-brand-100">Local SEO Dominance</div>
              <div className="px-6 py-3 bg-brand-50 rounded-full text-brand-700 font-bold border border-brand-100">Google Business Profile Authority</div>
              <div className="px-6 py-3 bg-brand-50 rounded-full text-brand-700 font-bold border border-brand-100">High-Conversion Web Architecture</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Contact = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [isVerified, setIsVerified] = useState(false);
  const [captchaInput, setCaptchaInput] = useState("");
  const [captchaError, setCaptchaError] = useState("");
  const [captchaQuestion, setCaptchaQuestion] = useState({ q: "4 + 3", a: 7 });
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    service: "SEO Strategy",
    message: ""
  });

  const generateCaptcha = () => {
    const n1 = Math.floor(Math.random() * 8) + 2; // 2 to 9
    const n2 = Math.floor(Math.random() * 8) + 1; // 1 to 8
    setCaptchaQuestion({ q: `${n1} + ${n2}`, a: n1 + n2 });
    setCaptchaInput("");
    setIsVerified(false);
    setCaptchaError("");
  };

  useEffect(() => {
    generateCaptcha();
  }, []);

  const handleCaptchaChange = (val: string) => {
    setCaptchaInput(val);
    if (parseInt(val.trim(), 10) === captchaQuestion.a) {
      setIsVerified(true);
      setCaptchaError("");
    } else {
      setIsVerified(false);
    }
  };

  const handleCheckboxClick = () => {
    if (isVerified) return;
    if (parseInt(captchaInput.trim(), 10) === captchaQuestion.a) {
      setIsVerified(true);
      setCaptchaError("");
    } else {
      setCaptchaError("Please solve the quick math problem to verify you are human.");
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (honeypot.trim() !== "") {
      // Silently discard automated bot submission
      setIsSubmitted(true);
      return;
    }
    if (!isVerified) {
      setCaptchaError("Please complete the security check to prove you are human.");
      return;
    }
    
    setIsSubmitting(true);
    try {
      await fetch("https://formsubmit.co/ajax/hello@dotsondigital.com", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({
          _subject: `New Strategy Inquiry from ${formData.name}`,
          _template: "table",
          _captcha: "false",
          "Full Name": formData.name,
          "Email Address": formData.email,
          "Service Needed": formData.service,
          "Message": formData.message
        })
      });
    } catch (err) {
      console.error("Form delivery error:", err);
    } finally {
      setIsSubmitting(false);
      setIsSubmitted(true);
    }
  };

  const handleReset = () => {
    setIsSubmitted(false);
    setFormData({
      name: "",
      email: "",
      service: "SEO Strategy",
      message: ""
    });
    generateCaptcha();
  };

  return (
    <section id="contact" className="py-24 bg-brand-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="bg-white rounded-[3rem] shadow-2xl overflow-hidden grid lg:grid-cols-2">
          <div className="p-12 lg:p-20 bg-brand-900 text-white">
            <h2 className="text-4xl font-bold mb-8">Let's Talk Strategy</h2>
            <p className="text-brand-100/80 text-lg mb-12">
              Ready to take your business to the next level? Get in touch with us today for a free digital audit and consultation.
            </p>
            
            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center text-brand-400">
                  <Phone className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-sm text-white/60 uppercase tracking-wider font-bold">Call Us</p>
                  <p className="text-xl font-semibold">(888) 899-1707</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center text-brand-400">
                  <MapPin className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-sm text-white/60 uppercase tracking-wider font-bold">Location</p>
                  <p className="text-xl font-semibold">Grovetown, GA 30813</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center text-brand-400">
                  <MessageSquare className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-sm text-white/60 uppercase tracking-wider font-bold">Direct Inquiry</p>
                  <p className="text-xl font-semibold">Online Client Intake</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="p-12 lg:p-20">
            {isSubmitted ? (
              <div 
                className="h-full flex flex-col items-center justify-center text-center space-y-6 animate-scaleIn"
              >
                <div className="w-20 h-20 bg-brand-100 rounded-full flex items-center justify-center text-brand-600">
                  <CheckCircle2 className="w-12 h-12" />
                </div>
                <h3 className="text-3xl font-bold text-brand-900">Message Sent!</h3>
                <p className="text-gray-600 text-lg max-w-sm">
                  Thank you for reaching out. We've received your inquiry and will be in touch shortly. Your information has been securely dispatched to our strategy team.
                </p>
                <button 
                  onClick={handleReset}
                  className="text-brand-600 font-bold hover:underline"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Honeypot field - hidden from humans, catches automated spam bots */}
                <div className="hidden" aria-hidden="true" style={{ display: "none" }}>
                  <label htmlFor="hp_contact_filter">Leave this field blank</label>
                  <input
                    type="text"
                    id="hp_contact_filter"
                    name="hp_contact_filter"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-bold text-gray-700 mb-2">Full Name</label>
                    <input 
                      id="contact-name"
                      required
                      type="text" 
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 outline-none transition-all"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-bold text-gray-700 mb-2">Email Address</label>
                    <input 
                      id="contact-email"
                      required
                      type="email" 
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 outline-none transition-all"
                      placeholder="john@company.com"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-service" className="block text-sm font-bold text-gray-700 mb-2">Service Needed</label>
                  <select 
                    id="contact-service"
                    value={formData.service}
                    onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 outline-none transition-all bg-white"
                  >
                    <option>SEO Strategy</option>
                    <option>PPC Management</option>
                    <option>Web Development</option>
                    <option>Google Business Profile Automation</option>
                    <option>Social Media Strategy</option>
                    <option>Email Marketing</option>
                    <option>Full Digital Audit</option>
                    <option>Other</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-bold text-gray-700 mb-2">Message</label>
                  <textarea 
                    id="contact-message"
                    required
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 outline-none transition-all"
                    placeholder="Tell us about your goals..."
                  />
                </div>

                {/* Anti-Spam Captcha Verification */}
                <div 
                  className={`p-4 rounded-2xl border transition-all ${
                    captchaError 
                      ? "border-red-300 bg-red-50/50 ring-2 ring-red-400/20" 
                      : isVerified 
                        ? "border-emerald-300 bg-emerald-50/40" 
                        : "border-gray-200 bg-slate-50/80"
                  }`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={handleCheckboxClick}
                        aria-label="Verify you are human"
                        className={`w-7 h-7 rounded-lg border flex items-center justify-center transition-all ${
                          isVerified 
                            ? "bg-emerald-600 border-emerald-600 text-white shadow-sm" 
                            : "bg-white border-gray-300 hover:border-brand-500 cursor-pointer shadow-inner"
                        }`}
                      >
                        {isVerified && <CheckCircle2 className="w-5 h-5 text-white" />}
                      </button>
                      <div>
                        <div className="flex items-center gap-1.5">
                          <span className={`text-sm font-bold ${isVerified ? "text-emerald-900" : "text-gray-800"}`}>
                            {isVerified ? "Human Verification Confirmed" : "I am not a robot"}
                          </span>
                          {isVerified ? (
                            <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0" />
                          ) : (
                            <Shield className="w-4 h-4 text-gray-400 shrink-0" />
                          )}
                        </div>
                        <p className="text-xs text-gray-500">
                          {isVerified ? "Spam protection passed" : "Anti-spam verification required"}
                        </p>
                      </div>
                    </div>

                    {!isVerified ? (
                      <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-xl border border-gray-200 shadow-sm self-start sm:self-auto">
                        <span className="text-xs font-semibold text-gray-700 select-none">
                          Solve: <span className="font-mono text-brand-600 font-bold text-sm">{captchaQuestion.q}</span> =
                        </span>
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={3}
                          value={captchaInput}
                          onChange={(e) => handleCaptchaChange(e.target.value)}
                          placeholder="?"
                          className="w-12 text-center font-bold text-sm px-1.5 py-1 rounded-lg border border-gray-300 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 outline-none"
                        />
                        <button
                          type="button"
                          onClick={generateCaptcha}
                          title="Get a different question"
                          className="text-gray-400 hover:text-brand-600 p-1.5 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                        >
                          <RefreshCw className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-white px-3 py-1.5 rounded-xl border border-emerald-200 shadow-sm self-start sm:self-auto">
                        <span>Verified</span>
                        <button
                          type="button"
                          onClick={generateCaptcha}
                          title="Reset security check"
                          className="text-gray-400 hover:text-brand-600 ml-1 p-0.5 cursor-pointer"
                        >
                          <RefreshCw className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>

                  {captchaError && (
                    <div className="flex items-center gap-1.5 text-xs font-medium text-red-600 mt-2.5 pt-2 border-t border-red-200/60">
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                      <span>{captchaError}</span>
                    </div>
                  )}
                </div>

                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full bg-brand-600 hover:bg-brand-700 text-white py-4 rounded-xl font-bold text-lg shadow-xl shadow-brand-600/20 transition-all cursor-pointer flex items-center justify-center gap-2 disabled:opacity-75"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Sending Inquiry...</span>
                    </>
                  ) : (
                    <>
                      <ShieldCheck className="w-5 h-5 text-brand-200" />
                      <span>Send Message</span>
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const LocationMapSection = () => {
  return (
    <section className="bg-gray-50/80 py-16 sm:py-20 border-t border-gray-100">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-10 gap-6">
          <div>
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-brand-50 border border-brand-100 text-brand-700 text-xs sm:text-sm font-semibold mb-3">
              <MapPin className="w-4 h-4 text-brand-600" />
              <span>Headquarters & Service Area</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-brand-900 tracking-tight">
              Proudly Based in <span className="text-brand-600">Grovetown, GA</span>
            </h2>
            <p className="text-gray-600 text-base sm:text-lg mt-2 max-w-2xl">
              Serving businesses across Grovetown, Augusta, Columbia County, and clients nationwide with high-performance digital marketing, web design, and SEO.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
            <div className="flex items-center gap-2 bg-white px-4 py-2.5 rounded-xl border border-gray-200/80 shadow-xs">
              <MapPin className="w-4 h-4 text-brand-600 shrink-0" />
              <span className="font-medium text-gray-900">Grovetown, GA 30813</span>
            </div>
            <a 
              href="tel:8888991707" 
              className="flex items-center gap-2 bg-white px-4 py-2.5 rounded-xl border border-gray-200/80 shadow-xs hover:border-brand-300 hover:text-brand-600 transition-colors"
            >
              <Phone className="w-4 h-4 text-brand-600 shrink-0" />
              <span className="font-medium text-gray-900">(888) 899-1707</span>
            </a>
          </div>
        </div>

        {/* Map Container */}
        <div className="relative w-full h-[380px] sm:h-[450px] rounded-3xl overflow-hidden shadow-xl border border-gray-200 bg-gray-100">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d53262.55471493427!2d-82.24878860408178!3d33.451659851714936!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88f9d6105a054f1d%3A0x88790d80b93ab6ba!2sGrovetown%2C%20GA%2030813!5e0!3m2!1sen!2sus!4v1790290529662!5m2!1sen!2sus"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
            title="Dotson Digital Location - Grovetown, GA 30813"
            className="w-full h-full"
          />
        </div>
      </div>
    </section>
  );
};

const Footer = () => {
  return (
    <footer className="bg-white py-12 border-t">
      <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-8">
        <div className="flex items-center gap-2">
          <img
            src="/dotsondigital-logo.webp"
            srcSet="/dotsondigital-logo.webp 1x, /dotsondigital-logo-80.webp 2x"
            alt="Dotson Digital"
            width={32}
            height={32}
            className="w-8 h-8 object-contain"
            loading="lazy"
            decoding="async"
            referrerPolicy="no-referrer"
          />
          <span 
            style={{ fontFamily: 'var(--font-heading)' }}
            className="text-xl font-extrabold tracking-tight text-brand-900"
          >
            DOTSON<span className="text-brand-500">DIGITAL</span>
          </span>
        </div>
        
        <p className="text-gray-500 text-sm">
          © {new Date().getFullYear()} Dotson Digital. All rights reserved.
        </p>
        
        <div className="flex items-center gap-6">
          <Link to="/work-with-us" className="text-gray-600 hover:text-brand-600 transition-colors">Work With Us</Link>
          <Link to="/privacy" className="text-gray-600 hover:text-brand-600 transition-colors">Privacy Policy</Link>
          <Link to="/terms" className="text-gray-600 hover:text-brand-600 transition-colors">Terms of Service</Link>
        </div>
      </div>
    </footer>
  );
};

const HomePage = () => (
  <>
    <Hero />
    <Services />
    <WhyChooseUs />
    <OurProcess />
    <LocalSEOSection />
    <FAQ />
    <Contact />
  </>
);

export default function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

function AppContent() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    document.title = "Dotson Digital - Your Partner for Local Web Design & Digital Marketing";
  }, []);

  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
    } else {
      const id = hash.replace("#", "");
      const timer = setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [pathname, hash]);

  useEffect(() => {
    if (typeof window !== "undefined" && Array.isArray((window as any).dataLayer)) {
      (window as any).dataLayer.push({
        event: "page_view",
        page_path: pathname,
      });
    }
  }, [pathname]);

  return (
    <div className="antialiased min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow">
        <Suspense fallback={<div className="min-h-screen bg-white" />}><Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPageContent />} />
          <Route path="/work-with-us" element={<WorkWithUsPageContent />} />
          <Route path="/dotson-hub" element={<DotsonHubPageContent />} />
          <Route path="/services/dotson-hub" element={<DotsonHubPageContent />} />
          <Route path="/services/:slug" element={<ServicePage />} />
          <Route path="/privacy" element={<PrivacyPageContent />} />
          <Route path="/terms" element={<TermsPageContent />} />
          <Route path="/contact" element={<HomePage />} />
          <Route path="/services" element={<HomePage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes></Suspense>
      </main>
      <LocationMapSection />
      <Footer />
    </div>
  );
}
